/**
 * How the screen is divided into views.
 *
 * - single: one view (the local run, or one racer for spectators) with other racers as ghosts;
 * - split: the local view plus up to MAX_SPLIT_OTHERS views that follow other racers (or, for
 *   spectators, up to MAX_SPECTATOR_VIEWS racers);
 * - auto: split on big screens (tablets, computers) when there is somebody to show, single
 *   on phones.
 *
 * Views are wide strips stacked from the top on landscape screens (the game scrolls
 * sideways, so wide views show the most); portrait and very tall screens stack them all
 * vertically. The local view is always the largest. Phones in portrait get a "rotate" hint.
 */
import type { PlayerId } from '@cube-rush/shared';
import type { LayoutPreference } from './contract';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type LayoutMode = 'single' | 'split';

/** Screens whose shorter side is at least this many CSS px count as big (tablet, computer). */
export const BIG_SCREEN_MIN_SIDE = 600;
export const MAX_SPLIT_OTHERS = 3;
export const MAX_SPECTATOR_VIEWS = 4;
/** Space between views, CSS px. */
export const VIEW_GAP = 4;

export function isBigScreen(width: number, height: number): boolean {
  return Math.min(width, height) >= BIG_SCREEN_MIN_SIDE;
}

/** A phone held upright: the game asks to rotate it (the race goes on underneath). */
export function needsRotateHint(width: number, height: number): boolean {
  return !isBigScreen(width, height) && height > width;
}

export function resolveLayout(
  preference: LayoutPreference,
  width: number,
  height: number,
  others: number,
): LayoutMode {
  if (others <= 0) return 'single';
  if (preference === 'auto') return isBigScreen(width, height) ? 'split' : 'single';
  return preference;
}

/**
 * Rectangles of `count` views in a `width` × `height` area. With `main`, the first view is
 * the local player's and gets the most room.
 */
export function arrangeViews(count: number, width: number, height: number, main: boolean): Rect[] {
  const full: Rect = { x: 0, y: 0, w: width, h: height };
  if (count <= 1) return [full];
  const tall = height > width * 1.05;
  if (tall) {
    const shares = main ? mainColumnShares(count) : new Array<number>(count).fill(1 / count);
    return stackRows(shares, width, height);
  }
  if (main) {
    const others = count - 1;
    const mainShare = others === 1 ? 0.6 : 0.62;
    const [top, bottom] = stackRows([mainShare, 1 - mainShare], width, height);
    return [top!, ...splitColumns(others, bottom!)];
  }
  if (count === 2) return stackRows([0.5, 0.5], width, height);
  const [top, bottom] = stackRows([0.5, 0.5], width, height);
  if (count === 3) return [top!, ...splitColumns(2, bottom!)];
  return [...splitColumns(2, top!), ...splitColumns(count - 2, bottom!)];
}

/** Height shares of a vertical stack with the main view first. */
function mainColumnShares(count: number): number[] {
  const mainShare = count === 2 ? 0.55 : count === 3 ? 0.5 : 0.46;
  const rest = (1 - mainShare) / (count - 1);
  return [mainShare, ...new Array<number>(count - 1).fill(rest)];
}

/** Full-width rows with the given height shares, separated by VIEW_GAP. */
function stackRows(shares: readonly number[], width: number, height: number): Rect[] {
  const usable = height - VIEW_GAP * (shares.length - 1);
  const rects: Rect[] = [];
  let y = 0;
  shares.forEach((share, i) => {
    const last = i === shares.length - 1;
    const h = last ? height - y : Math.round(usable * share);
    rects.push({ x: 0, y, w: width, h });
    y += h + VIEW_GAP;
  });
  return rects;
}

/** `count` equal columns inside `area`, separated by VIEW_GAP. */
function splitColumns(count: number, area: Rect): Rect[] {
  const usable = area.w - VIEW_GAP * (count - 1);
  const rects: Rect[] = [];
  let x = area.x;
  for (let i = 0; i < count; i++) {
    const last = i === count - 1;
    const w = last ? area.x + area.w - x : Math.round(usable / count);
    rects.push({ x, y: area.y, w, h: area.h });
    x += w + VIEW_GAP;
  }
  return rects;
}

/**
 * CSS px at the bottom of `view` covered by HUD controls along the bottom edge (`controls`:
 * their boxes in the same coordinates). Only controls across the view's lower half count.
 */
export function bottomInset(view: Rect, controls: readonly Rect[]): number {
  const bottom = view.y + view.h;
  let inset = 0;
  for (const box of controls) {
    const across = box.x < view.x + view.w && box.x + box.w > view.x;
    if (!across || box.y >= bottom || box.y < view.y + view.h / 2) continue;
    inset = Math.max(inset, Math.round(bottom - box.y));
  }
  return inset;
}

/** A racer that could get its own view. */
export interface ViewCandidate {
  id: PlayerId;
  /** Overall race progress 0..1. */
  progress: number;
  /** Still racing and sending positions (not finished, not gone, not stale). */
  active: boolean;
}

/**
 * Racers ordered by how interesting they are to watch: for a racer (`mine` = own progress)
 * the leader ahead first, then whoever is closest; for spectators (`mine` = null) the leaders.
 */
export function rankCandidates(
  candidates: readonly ViewCandidate[],
  mine: number | null,
): PlayerId[] {
  const active = candidates.filter((c) => c.active);
  if (mine === null) {
    return active.sort((a, b) => b.progress - a.progress).map((c) => c.id);
  }
  const byDistance = [...active].sort(
    (a, b) => Math.abs(a.progress - mine) - Math.abs(b.progress - mine),
  );
  const leader = active.reduce<ViewCandidate | null>(
    (best, c) => (best === null || c.progress > best.progress ? c : best),
    null,
  );
  if (!leader || leader.progress <= mine) return byDistance.map((c) => c.id);
  return [leader.id, ...byDistance.filter((c) => c.id !== leader.id).map((c) => c.id)];
}

/**
 * Up to `max` racers to show. Racers already shown (`current`) keep their place while they
 * stay among the top `max + 1` (so views do not flicker between two equally close racers);
 * free places go to the most relevant others.
 */
export function pickViews(
  candidates: readonly ViewCandidate[],
  mine: number | null,
  max: number,
  current: readonly PlayerId[],
): PlayerId[] {
  const ranked = rankCandidates(candidates, mine);
  const picked: PlayerId[] = [];
  for (const id of current) {
    const rank = ranked.indexOf(id);
    if (picked.length < max && rank >= 0 && rank <= max) picked.push(id);
  }
  for (const id of ranked) {
    if (picked.length >= max) break;
    if (!picked.includes(id)) picked.push(id);
  }
  return picked;
}
